import { makeAuthenticatedRequest } from './api'; // Use the correct exported function

/**
 * Fetches the application configuration from the backend.
 * @returns {Promise<object>} A promise that resolves to the configuration data.
 */
export const getAppConfig = async () => {
  try {
    // Use makeAuthenticatedRequest with GET method
    const responseData = await makeAuthenticatedRequest('/config/app', { method: 'GET' });

    // Assuming the backend response structure is { status: 'success', data: {...} }
    // and makeAuthenticatedRequest returns responseData.data or responseData
    // Adjust based on the actual structure returned by makeAuthenticatedRequest
    // If makeAuthenticatedRequest already extracts the nested 'data', use responseData directly.
    // If it returns the full { status: 'success', data: {...} }, check status and return responseData.data.

    // Let's assume makeAuthenticatedRequest returns the full response object for now
    // based on its implementation returning responseData.data || responseData
    if (responseData && responseData.status === 'success' && responseData.data) {
        return responseData.data; // Return the nested data object
    } else if (responseData && !responseData.status) {
        // Handle cases where makeAuthenticatedRequest might return the data directly
        // This depends on whether the /config/app endpoint itself nests the data
        // If the endpoint returns the config directly, this might be the correct path
        console.warn("Config response structure might not have 'status'. Assuming direct data.");
        return responseData; // Assume responseData is the config itself
    } else {
        throw new Error(responseData?.message || 'Failed to fetch app configuration: Invalid response structure');
    }
  } catch (error) {
    console.error('Error fetching app configuration:', error);
    // Rethrow or handle error as appropriate for your application
    throw error;
  }
};

/**
 * Fetch the automated-alert settings (Quote-Pending follow-up cadence/toggles). Admin-only.
 * makeAuthenticatedRequest unwraps one nesting level, so this resolves to the config object itself.
 * @returns {Promise<{quotePending: {enabled, email:{enabled,everyHours}, sms:{enabled,everyHours}}}>}
 */
export const getAutomatedAlerts = async () =>
  makeAuthenticatedRequest('/config/automated-alerts', { method: 'POST' });

/**
 * Persist the automated-alert settings. Admin-only.
 * @param {object} payload - { quotePending: {...} }
 */
export const updateAutomatedAlerts = async (payload) =>
  makeAuthenticatedRequest('/config/automated-alerts/update', { method: 'POST', body: payload });
